import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { savePushToken } from "./auth/api";
import { withAuthRetry } from "./auth/session";

/**
 * Push notification registration — native (Capacitor + FCM) and web (Firebase
 * web SDK + the /firebase-messaging-sw.js service worker). Both produce an
 * FCM token stored on the signed-in account (`POST /auth/push-token`, deduped
 * server-side), so the server sends to phones and browsers identically.
 *
 * Entirely fail-soft: missing Firebase config, denied permission, unsupported
 * browser, or a server hiccup just mean no push on this device — never a boot
 * failure. A focused tab / foregrounded app shows no banner (web: no
 * onMessage handler; native: no foreground presentation configured).
 */

/** Shape of the `self.FIREBASE_WEB` global from public/firebase-config.js
 * (single source, shared with the messaging service worker). */
interface FirebaseWebConfig {
  config: Record<string, string>;
  vapidKey: string;
}

declare global {
  // eslint-disable-next-line no-var
  var FIREBASE_WEB: FirebaseWebConfig | null | undefined;
}

function storeToken(token: string): void {
  void withAuthRetry((accessToken) => savePushToken(token, accessToken)).catch(
    (err: unknown) => {
      console.warn("[push] failed to store device token:", err);
    },
  );
}

/**
 * Called on every authenticated app boot (main.tsx) so a login on a new
 * device always records that device's token. Native: request permission and
 * register. Web: silently refresh the token ONLY when permission was already
 * granted — the permission prompt needs a user gesture (the Account tab's
 * "Enable on this browser" button, `enableWebPush`).
 */
export async function registerPushNotifications(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    try {
      let status = await PushNotifications.checkPermissions();
      if (status.receive === "prompt") {
        status = await PushNotifications.requestPermissions();
      }
      if (status.receive !== "granted") return;

      await PushNotifications.addListener("registration", ({ value }) => {
        storeToken(value);
      });
      await PushNotifications.register();
    } catch (err) {
      console.warn("[push] registration unavailable:", err);
    }
    return;
  }
  if (webPushStatus() === "granted") await registerWebPush();
}

/** Where this browser stands with push — drives the Account tab's browser row.
 * `unsupported` covers native (no row needed), missing Firebase web config,
 * and browsers without the Push API. */
export type WebPushStatus = "unsupported" | "denied" | "prompt" | "granted";

export function webPushStatus(): WebPushStatus {
  if (Capacitor.isNativePlatform()) return "unsupported";
  if (
    !globalThis.FIREBASE_WEB ||
    !("serviceWorker" in navigator) ||
    !("Notification" in window) ||
    !("PushManager" in window)
  ) {
    return "unsupported";
  }
  const p = Notification.permission;
  return p === "granted" ? "granted" : p === "denied" ? "denied" : "prompt";
}

/** User-gesture path: ask for permission, then register + store the token.
 * Returns the resulting status for the UI. */
export async function enableWebPush(): Promise<WebPushStatus> {
  if (webPushStatus() === "prompt") await Notification.requestPermission();
  const status = webPushStatus();
  if (status === "granted") await registerWebPush();
  return status;
}

async function registerWebPush(): Promise<void> {
  const fw = globalThis.FIREBASE_WEB;
  if (!fw) return;
  try {
    // Dynamic imports keep the Firebase SDK out of the main bundle (and off
    // unconfigured deployments entirely).
    const [{ getApps, initializeApp }, { getMessaging, getToken, isSupported }] =
      await Promise.all([import("firebase/app"), import("firebase/messaging")]);
    if (!(await isSupported())) return;
    const app = getApps()[0] ?? initializeApp(fw.config);
    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
    );
    const token = await getToken(getMessaging(app), {
      vapidKey: fw.vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (token) storeToken(token);
  } catch (err) {
    console.warn("[push] web registration unavailable:", err);
  }
}
